import React, { useCallback, useMemo, useState } from "react";
import { format, addMonths, subMonths, addWeeks, subWeeks, addDays, subDays } from "date-fns";
import { useSelector } from "react-redux";
import { RootState } from "@/redux/store";
import { Card } from "@/components/ui/card";
import {
  Calendar as FullCalendar,
  type CalendarEvent,
  type CalendarViewType,
} from "@/components/ui/full-calendar/calendar";
import FacilityScheduleForm from "../modal/form";
import { FacilitySchedule } from "@/types/facility/schedule";
import {
  useGetAllFacilityScheduleSlotQuery,
  useDeleteFacilityScheduleMutation,
  useLazyGetFacilityScheduleSlotDetailsQuery,
  useGetFacilitiesQuery,
} from "@/services/facilityApis";
import { toast } from "@/components/ui/use-toast";
import { ErrorType } from "@/types";
import type { DeleteScope } from "@/components/ui/full-calendar/calendar-event-actions";
import { deriveEndDateFromCount } from "@/utils/scheduleUtils";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from "@/components/ui/sheet";
import { Separator } from "@/components/ui/separator";
import { MultiSelect } from "@/components/ui/multiselect/multiselectCheckbox";
import { Button } from "@/components/ui/button";

// ─────────────────────────────────────────────────────────────────────────────
//  Helpers
// ─────────────────────────────────────────────────────────────────────────────
const colorFromString = (colorClass?: string): string => {
  if (!colorClass) return "blue";
  return colorClass.split("-")[0];
};

/** "2026-04-20T14:00:00" → "14:00" */
const extractTime = (iso: string): string => {
  if (!iso) return "";
  if (iso.includes("T")) return iso.split("T")[1].substring(0, 5);
  return iso.substring(0, 5);
};

/** "2026-04-20T14:00:00" → "2026-04-20" */
const extractDate = (iso: string): string => {
  if (!iso) return "";
  if (iso.includes("T")) return iso.split("T")[0];
  return iso;
};

/**
 * Parse rrule_str into RecurrenceConfig object that RecurrenceSelector expects.
 *
 * Example input:
 *   "RRULE:FREQ=WEEKLY;BYDAY=MO,WE;UNTIL=20260507T000000Z"
 *
 * Output:
 *   {
 *     frequency: "weekly",
 *     interval: 1,
 *     weekDays: ["MO", "WE"],
 *     endCondition: "on_date",
 *     endDate: "2026-05-07",
 *     occurrences: undefined,
 *   }
 */
const parseRRuleStr = (rruleStr: string): Record<string, any> | undefined => {
  if (!rruleStr) return undefined;

  // Strip "RRULE:" prefix if present
  const raw = rruleStr.replace(/^RRULE:/i, "");

  // Parse key=value pairs
  const parts: Record<string, string> = {};
  raw.split(";").forEach((part) => {
    const [key, val] = part.split("=");
    if (key && val !== undefined) parts[key.toUpperCase()] = val;
  });

  const frequency = (parts["FREQ"] ?? "").toLowerCase(); // "weekly", "daily", etc.
  const interval = parts["INTERVAL"] ? parseInt(parts["INTERVAL"], 10) : 1;

  // BYDAY → array of day codes e.g. ["MO", "WE"]
  const mapRecurrenceDay = (dayCode: string, full: boolean = false) => {
    const normalized = dayCode.trim().toUpperCase();
    const code = normalized.replace(/^[-+]?\d+/, "");
    const map: Record<string, string> = {
      MO: full ? "Monday" : "Mon",
      TU: full ? "Tuesday" : "Tue",
      WE: full ? "Wednesday" : "Wed",
      TH: full ? "Thursday" : "Thu",
      FR: full ? "Friday" : "Fri",
      SA: full ? "Saturday" : "Sat",
      SU: full ? "Sunday" : "Sun",
    };
    return map[code] ?? dayCode.trim();
  };

  const weekDays = parts["BYDAY"] && frequency !== "monthly"
    ? parts["BYDAY"].split(",").map((d) => mapRecurrenceDay(d))
    : [];

  // Monthly specific parsing
  let monthlyType: "day" | "pattern" = "day";
  let monthlyDay: number = 1;
  let monthlyPattern: any = { week: "first", day: "Monday" };

  if (frequency === "monthly") {
    if (parts["BYMONTHDAY"]) {
      monthlyType = "day";
      monthlyDay = parseInt(parts["BYMONTHDAY"], 10);
    } else if (parts["BYDAY"]) {
      monthlyType = "pattern";
      const rawByDay = parts["BYDAY"];
      const match = rawByDay.match(/^([-+]?\d+)([A-Z]{2})$/);
      if (match) {
        const pos = match[1];
        const dayCode = match[2];
        const posMap: Record<string, string> = {
          "1": "first",
          "2": "second",
          "3": "third",
          "4": "fourth",
          "-1": "last",
        };
        monthlyPattern = {
          week: posMap[pos] || "first",
          day: mapRecurrenceDay(dayCode, true),
        };
      }
    }
  }

  let endCondition: string = "after";
  let endDate: string | undefined;
  let occurrences: number | undefined;

  if (parts["UNTIL"]) {
    endCondition = "on_date";
    const u = parts["UNTIL"].replace(/T.*$/, ""); // "20260507"
    endDate = `${u.substring(0, 4)}-${u.substring(4, 6)}-${u.substring(6, 8)}`;
  } else if (parts["COUNT"]) {
    endCondition = "after";
    occurrences = parseInt(parts["COUNT"], 10);
  }

  if (!frequency) return undefined;

  return {
    enabled: true,
    frequency,
    interval,
    weekDays,
    monthlyType,
    monthlyDay,
    monthlyPattern,
    endCondition,
    endDate,
    occurrences,
  };
};

// ─────────────────────────────────────────────────────────────────────────────
//  Component
// ─────────────────────────────────────────────────────────────────────────────
const FacilityScheduleCalendarView: React.FC = () => {
  const orgId =
    useSelector((state: RootState) => state.auth.userInfo?.user?.org_id) || 0;

  const facilityScheduleAccess = (() => {
    try {
      return (
        JSON.parse(localStorage.getItem("accessLevels") as string)
          .facility_schedule ?? "no_access"
      );
    } catch {
      return "no_access";
    }
  })();

  // ── Modal / form state ──────────────────────────────────────────────
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [action, setAction] = useState<"add" | "edit">("add");
  const [formData, setFormData] = useState<FacilitySchedule | undefined>(undefined);
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);

  // ── Calendar navigation & View ──────────────────────────────────────
  const [currentDate, setCurrentDate] = useState<Date>(new Date());
  const [calendarView, setCalendarView] = useState<CalendarViewType>("month");

  // ── Filter sheet state ──────────────────────────────────────────────
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [pendingFacilityIds, setPendingFacilityIds] = useState<string[]>([]);
  const [appliedFacilityIds, setAppliedFacilityIds] = useState<string[]>([]);

  // ── Fetch facilities for filter dropdown ────────────────────────────
  const { data: facilitiesData } = useGetFacilitiesQuery(
    { orgId, query: "sort_key=id&sort_order=desc" },
    { skip: !orgId }
  );

  const facilityOptions = useMemo(() => {
    return Array.isArray(facilitiesData?.data)
      ? facilitiesData.data.map((f: any) => ({ value: String(f.id), label: f.name }))
      : [];
  }, [facilitiesData]);

  const handlePrevious = useCallback(() => {
    setCurrentDate((prev) => {
      if (calendarView === "month") return subMonths(prev, 1);
      if (calendarView === "week") return subWeeks(prev, 1);
      return subDays(prev, 1);
    });
  }, [calendarView]);

  const handleNext = useCallback(() => {
    setCurrentDate((prev) => {
      if (calendarView === "month") return addMonths(prev, 1);
      if (calendarView === "week") return addWeeks(prev, 1);
      return addDays(prev, 1);
    });
  }, [calendarView]);

  // ── API: fetch slots for visible month ──────────────────────────────
  const queryString = useMemo(() => {
    const monthStart = new Date(currentDate.getFullYear(), currentDate.getMonth(), 1);
    const monthEnd = new Date(currentDate.getFullYear(), currentDate.getMonth() + 1, 0);
    const params = new URLSearchParams({
      start_date: format(monthStart, "yyyy-MM-dd"),
      end_date: format(monthEnd, "yyyy-MM-dd"),
      load_consumed_slots: "true",
    });
    if (appliedFacilityIds.length > 0) {
      params.set("facility_id", appliedFacilityIds.join(","));
    }
    return params.toString();
  }, [currentDate, appliedFacilityIds]);

  const { data: slotsData, isLoading, refetch } = useGetAllFacilityScheduleSlotQuery(
    { query: queryString, orgId },
    { skip: !orgId }
  );

  const [deleteFacilitySchedule] = useDeleteFacilityScheduleMutation();

  // ── Lazy query: fetch full schedule details when edit is clicked ─────
  const [getSlotDetails] = useLazyGetFacilityScheduleSlotDetailsQuery();

  // ── Transform API slots → CalendarEvents ───────────────────────────
  const events: CalendarEvent[] = useMemo(() => {
    if (!slotsData?.data) return [];
    return slotsData.data
      .map((slot) => {
        try {
          const start = new Date(`${slot.slot_date}T${slot.start_time}`);
          const end = new Date(`${slot.slot_date}T${slot.end_time}`);
          if (isNaN(start.getTime()) || isNaN(end.getTime())) return null;

          const isConsumed = slot.consumed_slots && slot.consumed_slots.length > 0;
          const color = isConsumed ? "red" : colorFromString(slot.color);
          const eventId = slot.id
            ? String(slot.id)
            : `${slot.facility_schedule_id}_${slot.slot_date}`
          return {
            id: eventId,
            title: `${slot.facility_name || slot.name || "Facility"}  ${format(start, "HH:mm")} – ${format(end, "HH:mm")}`,
            start,
            end,
            color,
            description: slot.location || "",
            tags: [
              {
                label: isConsumed ? "Booked" : "Available",
                color: isConsumed ? "red" : "green",
              },
            ],
            user: isConsumed
              ? {
                name: slot.consumed_slots?.[0]?.member_name || "Booked",
                avatar: slot.consumed_slots?.[0]?.profile_img,
              }
              : { name: "Available" },
            originalData: slot,
          } as CalendarEvent;
        } catch {
          return null;
        }
      })
      .filter(Boolean) as CalendarEvent[];
  }, [slotsData]);
  console.log("Facicly Schedule Events", events)
  // ── Handlers ────────────────────────────────────────────────────────
  const handleCreateNew = () => {
    setSelectedDate(undefined);
    setAction("add");
    setFormData(undefined);
    setIsDialogOpen(true);
  };

  const handleDateClick = (date: Date) => {
    setSelectedDate(date);
    setAction("add");
    setFormData(undefined);
    setIsDialogOpen(true);
  };

  /**
   * Edit click → GET /facility/schedule/slots/details
   *
   * Response: { data: { schedule_id, rrule_str, color, location,
   *   duration_per_slot, income_category_id, tax_group_id, member_only,
   *   advance_booking_days, break_duration, start_time (ISO), end_time (ISO),
   *   facility_id, max_attendees, price_master_id, addons[], refund_policy[] } }
   *
   * rrule_str is parsed into a recurrence object for RecurrenceSelector.
   */
  const handleEditEvent = async (event: CalendarEvent) => {
    if (!event.originalData) return;

    const slot = event.originalData;

    if (
      slot?.facility_schedule_id &&
      slot?.slot_date &&
      slot?.start_time &&
      slot?.end_time
    ) {
      try {
        const startTime = `${slot.slot_date}T${slot.start_time}`;
        const endTime = `${slot.slot_date}T${slot.end_time}`;

        const result = await getSlotDetails({
          org_id: orgId,
          schedule_id: slot.facility_schedule_id as number,
          start_time: startTime,
          end_time: endTime,
        }).unwrap();

        // API wraps response in { data: { ... } }
        const details = (result as any)?.data ?? result;

        // Parse rrule_str → recurrence config for RecurrenceSelector
        // e.g. "RRULE:FREQ=WEEKLY;BYDAY=MO,WE;UNTIL=20260507T000000Z"
        // → { frequency: "weekly", weekDays: ["MO","WE"], endCondition: "on_date", endDate: "2026-05-07" }
        const recurrence = parseRRuleStr(details.rrule_str ?? "");

        // Map into FacilitySchedule shape that the form expects
        const merged: FacilitySchedule = {
          org_id: orgId,
          general_info: {
            id: details.facility_schedule_id,
            facility_id: details.facility_id ?? 0,
            is_exclusive: details.is_exclusive ?? false,
            max_attendees: details.max_attendees,
            price_master_id: details.price_master_id,
            start_time: extractTime(details.start_time ?? ""),
            end_time: extractTime(details.end_time ?? ""),
            start_date: slot.start_date || extractDate(details.start_time ?? ""),
            end_date: slot.end_date || extractDate(details.end_time ?? ""),
            color: details.color ?? "",
            location: details.location ?? "",
            description: details.description ?? "",
            name: details.name ?? "",
            type: details.type ?? "facility_schedule",
            member_only: details.member_only ?? false,
            always_available: details.always_available ?? false,
            duration_per_slot: details.duration_per_slot ?? 0,
            break_duration: details.break_duration ?? 0,
            income_category_id: details.income_category_id,
            tax_group_id: details.tax_group_id,
            advance_booking_days: details.advance_booking_days,
            rrule_str: details.rrule_str ?? "",
            // Parsed recurrence object for RecurrenceSelector component
            recurrence: recurrence as any,
          },
          add_ons: Array.isArray(details.addons) ? details.addons : [],
          refund_policies: Array.isArray(details.refund_policy) ? details.refund_policy : [],
          tier_pricing: [],
          plan_pricing: [],
          group_pricing: [],
          special_pricing: [],
        };

        console.log(`this is merged data ${JSON.stringify(merged)}`)

        setAction("edit");
        setFormData(merged);
        setSelectedDate(undefined);
        setIsDialogOpen(true);
      } catch (error) {
        const typedError = error as ErrorType;
        toast({
          variant: "destructive",
          title: "Error loading schedule details",
          description: `${typedError.data?.detail ||
            typedError.data?.message ||
            "Something went wrong"
            }`,
        });
      }
    } else {
      // Fallback: open form with existing slot data
      setAction("edit");
      setFormData(slot as FacilitySchedule);
      setSelectedDate(undefined);
      setIsDialogOpen(true);
    }
  };

  /**
   * Scope-aware delete — backend ALWAYS needs start_date + end_date.
   */
  const handleDeleteEvent = async (
    event: CalendarEvent,
    scope: DeleteScope = "this"
  ) => {
    const slot = event.originalData;

    if (!slot?.facility_schedule_id) {
      toast({
        variant: "destructive",
        title: "Cannot delete",
        description: "This slot has no associated schedule.",
      });
      return;
    }

    const slotDate: string = slot.slot_date;
    let scheduleStartDate: string = slot.start_date || slotDate;
    let scheduleEndDate: string = slot.end_date || slotDate;

    // Fetch schedule details to get recurrence end date
    if (slot?.facility_schedule_id && slot?.slot_date && slot?.start_time && slot?.end_time) {
      try {
        const startTime = `${slot.slot_date}T${slot.start_time}`;
        const endTime = `${slot.slot_date}T${slot.end_time}`;

        const result = await getSlotDetails({
          org_id: orgId,
          schedule_id: slot.facility_schedule_id as number,
          start_time: startTime,
          end_time: endTime,
        }).unwrap();

        const details = (result as any)?.data ?? result;
        const recurrence = parseRRuleStr(details.rrule_str ?? "");

        // Extract schedule dates from API response
        if (details.start_time) {
          scheduleStartDate = extractDate(details.start_time);
        }

        // Use recurrence end date if available, otherwise calculate from count or use API end_date
        if (recurrence?.endDate) {
          scheduleEndDate = recurrence.endDate;
        } else if (recurrence?.occurrences && details.start_time) {
          const calcEnd = deriveEndDateFromCount(
            recurrence as any,
            extractDate(details.start_time)
          );
          if (calcEnd) scheduleEndDate = calcEnd;
        } else if (details.end_date) {
          scheduleEndDate = details.end_date;
        }
      } catch (error) {
        console.warn("Could not fetch schedule details", error);
      }
    }

    let start_date: string;
    let end_date: string;

    if (scope === "this") {
      start_date = slotDate;
      end_date = slotDate;
    } else if (scope === "this_and_following") {
      start_date = slotDate;
      end_date = scheduleEndDate;
    } else {
      start_date = scheduleStartDate;
      end_date = scheduleEndDate;
    }

    try {
      await deleteFacilitySchedule({
        schedule_id: slot.facility_schedule_id as number,
        scope,
        start_date,
        end_date,
      }).unwrap();

      toast({ variant: "success", title: "Schedule deleted successfully" });
      refetch();
    } catch (error) {
      const typedError = error as ErrorType;
      toast({
        variant: "destructive",
        title: "Error deleting schedule",
        description: `${typedError.data?.detail ||
          typedError.data?.message ||
          "Something went wrong"
          }`,
      });
    }
  };

  return (
    <div className="w-full h-full p-5 flex flex-col gap-4">
      <Card className="flex-1 overflow-hidden" style={{ minHeight: "600px" }}>
        <FullCalendar
          events={events}
          initialView="month"
          initialDate={currentDate}
          onViewEvent={handleEditEvent}
          onEditEvent={handleEditEvent}
          onDeleteEvent={handleDeleteEvent}
          onDateClick={handleDateClick}
          onPrevious={handlePrevious}
          onNext={handleNext}
          onCalendarViewChange={setCalendarView}
          access={facilityScheduleAccess}
          refetch={refetch}
          isCustomView={false}
          allowGridView={false}
          onFilterEvents={() => {
            setPendingFacilityIds(appliedFacilityIds);
            setIsFilterOpen(true);
          }}
          isFilterApplied={appliedFacilityIds.length > 0}
        />
      </Card>

      {/* ── Facility Filter Sheet ─────────────────────────────────── */}
      <Sheet open={isFilterOpen} onOpenChange={setIsFilterOpen}>
        <SheetContent className="!h-screen">
          <SheetHeader>
            <SheetTitle>Filters</SheetTitle>
          </SheetHeader>
          <Separator className="h-[1px] rounded-full my-2" />
          <div className="py-2 space-y-4">
            {/* Facility MultiSelect */}
            <MultiSelect
              floatingLabel="Facility"
              options={facilityOptions}
              defaultValue={pendingFacilityIds}
              onValueChange={(selected) => setPendingFacilityIds(selected as string[])}
              placeholder="Select Facility"
              variant="inverted"
              maxCount={2}
              className="capitalize"
            />

            {/* Action Buttons */}
            <div className="flex gap-3">
              <Button
                variant="outline"
                className="border-primary text-black font-semibold w-full"
                onClick={() => {
                  setPendingFacilityIds([]);
                  setAppliedFacilityIds([]);
                  setIsFilterOpen(false);
                }}
              >
                Reset
              </Button>
              <Button
                className="bg-primary text-black font-semibold w-full"
                onClick={() => {
                  setAppliedFacilityIds(pendingFacilityIds);
                  setIsFilterOpen(false);
                }}
              >
                <i className="fa fa-filter mr-2" />
                Apply Filter
              </Button>
            </div>
          </div>
        </SheetContent>
      </Sheet>

      <FacilityScheduleForm
        isOpen={isDialogOpen}
        setOpen={setIsDialogOpen}
        data={formData}
        action={action}
        setAction={setAction}
        setData={setFormData}
        refetch={refetch}
        selectedDate={selectedDate}
      />
    </div>
  );
};

export default FacilityScheduleCalendarView;